import React from "react";
import { motion } from "framer-motion";
import {
  Card,
  Button as AntButton,
  Typography,
  Tag,
  Row,
  Col,
  Input,
  Space,
  Tooltip,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

/**
 * AttorneyNumbersSection
 *
 * Props:
 *   -- shared --
 *   attorneyCount           — total number of attorney slots (from plan feature)
 *   isCheckingAttorney      — boolean loading flag
 *   isAttorney              — "1" | "0"
 *   handleCheckAttorneyStatus — function to trigger validation
 *
 *   -- create mode (isEditing = false) --
 *   attorneyNumbers         — string[] of all slots
 *   attorneyValidation      — { [num]: "valid"|"invalid" }
 *   attorneyValidationMessages — { [num]: string }
 *   attorneyTMCounts        — { [num]: number }
 *   handleAttorneyNumberChange — (index, value) => void
 *
 *   -- edit mode (isEditing = true) --
 *   isEditing               — boolean
 *   existingAttorneys       — [{ attorney_number, tm_count }]  (locked, read-only)
 *   emptySlots              — [{ value, status, message, tmCount, isChecking }]
 *   planLimit               — number (from plan feature)
 *   onSlotInputChange       — (index, value) => void
 *   onCheckSlotStatus       — (index) => void   (per-row check for empty slots)
 */
const AttorneyNumbersSection = ({
  // shared
  attorneyCount,
  isCheckingAttorney,
  isAttorney,
  handleCheckAttorneyStatus,

  // create mode
  attorneyNumbers = [],
  attorneyValidation = {},
  attorneyValidationMessages = {},
  attorneyTMCounts = {},
  handleAttorneyNumberChange,
  attorneyNameOptions = {},
  selectedAttorneyNames = {},
  onSelectAttorneyName,

  // edit mode
  isEditing = false,
  existingAttorneys = [],
  emptySlots = [],
  planLimit = 0,
  onSlotInputChange,
  onCheckSlotStatus,
  onTMCountChange,
}) => {
  if (attorneyCount === 0 && !isEditing) return null;
  if (isEditing && existingAttorneys.length === 0 && emptySlots.length === 0 && isAttorney !== "0") return null;

  const isCorporate = isAttorney === "0";

  // ── Edit mode rendering ─────────────────────────────────────────────────
  if (isEditing && !isCorporate) {
    const validNewSlots = emptySlots.filter(
      (s) => s.status === "valid" && s.value.trim(),
    );

    return (
      <Col xs={24}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            className="shadow-sm border"
            style={{
              background:
                "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
              borderRadius: 12,
              border: "2px solid #d3adf7",
            }}
          >
            {/* Header Section */}
            <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
              <div>
                <Title level={5} className="mb-1" style={{ color: "#531dab" }}>
                  <InfoCircleOutlined
                    className="me-2"
                    style={{ color: "#722ed1" }}
                  />
                  Attorney Numbers Management
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Existing attorneys are locked. Fill empty slots and click
                  "Check Status" to validate.
                </Text>
              </div>
              <Space>
                <Tag color="blue" style={{ fontSize: 12, fontWeight: 600 }}>
                  {existingAttorneys.length} Existing
                </Tag>
                {emptySlots.length > 0 && (
                  <Tag color="orange" style={{ fontSize: 12, fontWeight: 600 }}>
                    {emptySlots.length} Empty Slot
                    {emptySlots.length > 1 ? "s" : ""}
                  </Tag>
                )}
                {planLimit > 0 && (
                  <Tag
                    color="purple"
                    style={{ fontSize: 12, fontWeight: 600, borderRadius: 20 }}
                  >
                    Plan Limit: {planLimit}
                  </Tag>
                )}
              </Space>
            </div>

            <Row gutter={[16, 16]}>
              {/* ── Existing attorneys (LOCKED) ── */}
              {existingAttorneys.map((atty, index) => (
                <Col xs={24} key={`existing-${index}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.04 }}
                  >
                    <Card
                      size="small"
                      className="shadow-sm"
                      style={{
                        backgroundColor: "#f6ffed",
                        borderColor: "#52c41a",
                        borderWidth: 2,
                        borderRadius: 8,
                      }}
                    >
                      <Row gutter={12} align="middle">
                        {/* Attorney Number — read-only */}
                        <Col xs={24} sm={24} md={16} lg={16}>
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                            size={4}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <Text
                                strong
                                style={{ fontSize: 13, color: "#595959" }}
                              >
                                Attorney #{index + 1}
                              </Text>
                              <Tag
                                color="success"
                                icon={<CheckCircleOutlined />}
                                style={{ margin: 0, fontSize: 11 }}
                              >
                                Active
                              </Tag>
                            </div>

                            <Input
                              size="large"
                              value={atty.attorney_number}
                              disabled
                              prefix={
                                <FileProtectOutlined
                                  style={{ color: "#52c41a" }}
                                />
                              }
                              style={{
                                borderWidth: 2,
                                borderRadius: 8,
                                fontSize: 15,
                                fontWeight: 600,
                                backgroundColor: "#f6ffed",
                                borderColor: "#52c41a",
                                color: "#262626",
                                cursor: "not-allowed",
                                WebkitTextFillColor: "#262626",
                                opacity: 1,
                              }}
                            />

                            {/* Attorney Name Selection — edit mode */}
                            {attorneyNameOptions[atty.attorney_number]?.length > 0 && (() => {
                              const hasSelection = (selectedAttorneyNames[atty.attorney_number]?.length || 0) > 0;
                              return (
                                <div
                                  style={{
                                    marginTop: 6,
                                    padding: "8px 10px",
                                    borderRadius: 6,
                                    border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
                                    background: hasSelection ? "#f6ffed" : "#fff1f0",
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 11,
                                      color: hasSelection ? "#389e0d" : "#cf1322",
                                      fontWeight: 600,
                                      display: "block",
                                      marginBottom: 6,
                                    }}
                                  >
                                    {hasSelection ? "✓ Attorney Name Selected" : "⚠ Select Attorney Name:"}
                                  </Text>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {attorneyNameOptions[atty.attorney_number].map((opt) => {
                                      const isSelected = (selectedAttorneyNames[atty.attorney_number] || []).includes(opt.attorney_name);
                                      return (
                                        <div
                                          key={opt.attorney_name}
                                          onClick={() => onSelectAttorneyName && onSelectAttorneyName(atty.attorney_number, opt.attorney_name)}
                                          style={{
                                            cursor: "pointer",
                                            padding: "4px 10px",
                                            borderRadius: 6,
                                            fontSize: 12,
                                            fontWeight: isSelected ? 600 : 400,
                                            border: `1.5px solid ${isSelected ? "#52c41a" : "#d9d9d9"}`,
                                            background: isSelected ? "#f6ffed" : "#fafafa",
                                            color: isSelected ? "#389e0d" : "#595959",
                                            transition: "all 0.2s",
                                          }}
                                        >
                                          {opt.attorney_name}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </Space>
                        </Col>

                        {/* TM Count */}
                        <Col xs={24} sm={24} md={8} lg={8}>
                          <Card
                            size="small"
                            className="text-center shadow-sm"
                            style={{
                              background:
                                "linear-gradient(135deg, #52c41a15 0%, #73d13d15 100%)",
                              borderColor: "#52c41a",
                              borderWidth: 2,
                              borderRadius: 8,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              padding: "12px 8px",
                              minHeight: 90,
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                display: "block",
                                marginBottom: 4,
                              }}
                            >
                              TM Count
                            </Text>
                            {/* Read-only — this is the count the API returned.
                                Adjust the overall figure in Subscription Summary
                                → Total TM Count instead. */}
                            <Text
                              style={{
                                fontSize: 28, fontWeight: 700, color: "#52c41a",
                                textAlign: "center", display: "block", lineHeight: 1.1,
                              }}
                            >
                              {Number(atty.tm_count || 0).toLocaleString("en-IN")}
                            </Text>
                            <Text
                              type="secondary"
                              style={{ fontSize: 10, display: "block", marginTop: 2 }}
                            >
                              Trademarks
                            </Text>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </motion.div>
                </Col>
              ))}

              {/* ── Empty slots (editable) ── */}
              {emptySlots.map((slot, index) => {
                const rowNum = existingAttorneys.length + index + 1;
                const isValid = slot.status === "valid";
                const isInvalid = slot.status === "invalid";

                return (
                  <Col xs={24} key={`slot-${index}`}>
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.2,
                        delay: (existingAttorneys.length + index) * 0.04,
                      }}
                    >
                      <Card
                        size="small"
                        className="shadow-sm"
                        style={{
                          backgroundColor: isValid
                            ? "#f6ffed"
                            : isInvalid
                              ? "#fff1f0"
                              : "#ffffff",
                          borderColor: isValid
                            ? "#52c41a"
                            : isInvalid
                              ? "#ff4d4f"
                              : "#d9d9d9",
                          borderWidth: 2,
                          borderRadius: 8,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Row gutter={12} align="middle">
                          {/* Input */}
                          <Col xs={24} sm={24} md={16} lg={16}>
                            <Space
                              direction="vertical"
                              style={{ width: "100%" }}
                              size={4}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <Text
                                  strong
                                  style={{ fontSize: 13, color: "#595959" }}
                                >
                                  Attorney #{rowNum}
                                  <Tag
                                    color="blue"
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 10,
                                      lineHeight: "16px",
                                    }}
                                  >
                                    New
                                  </Tag>
                                </Text>
                                {slot.status && (
                                  <Tag
                                    color={isValid ? "success" : "error"}
                                    icon={
                                      isValid ? (
                                        <CheckCircleOutlined />
                                      ) : (
                                        <CloseCircleOutlined />
                                      )
                                    }
                                    style={{ margin: 0, fontSize: 11 }}
                                  >
                                    {isValid ? "Valid" : "Invalid"}
                                  </Tag>
                                )}
                              </div>

                              <Input
                                size="large"
                                value={slot.value}
                                onChange={(e) =>
                                  onSlotInputChange(index, e.target.value)
                                }
                                maxLength={10}
                                placeholder={`Enter attorney number ${rowNum}`}
                                suffix={
                                  slot.isChecking ? (
                                    <Spin size="small" />
                                  ) : isValid ? (
                                    <CheckCircleOutlined
                                      style={{ color: "#52c41a", fontSize: 20 }}
                                    />
                                  ) : isInvalid ? (
                                    <CloseCircleOutlined
                                      style={{ color: "#ff4d4f", fontSize: 20 }}
                                    />
                                  ) : (
                                    <InfoCircleOutlined
                                      style={{
                                        color: "rgba(0,0,0,.45)",
                                        fontSize: 18,
                                      }}
                                    />
                                  )
                                }
                                status={isInvalid ? "error" : ""}
                                style={{
                                  borderWidth: 2,
                                  borderRadius: 8,
                                  fontSize: 15,
                                  fontWeight: 500,
                                }}
                                onPressEnter={() => onCheckSlotStatus(index)}
                              />

                              {/* Validation message */}
                              {slot.message && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <Text
                                    className="d-block"
                                    type={isValid ? "success" : "danger"}
                                    style={{ fontSize: 12, fontWeight: 500 }}
                                  >
                                    <span style={{ marginRight: 4 }}>
                                      {isValid ? "✓" : "✗"}
                                    </span>
                                    {slot.message}
                                  </Text>
                                </motion.div>
                              )}

                              {/* Attorney Name Selection for validated empty slots */}
                              {isValid && attorneyNameOptions[slot.value]?.length > 0 && (() => {
                                const opts = attorneyNameOptions[slot.value];
                                const hasSelection = (selectedAttorneyNames[slot.value]?.length || 0) > 0;
                                return (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ duration: 0.2 }}
                                    style={{
                                      marginTop: 6,
                                      padding: "8px 10px",
                                      borderRadius: 6,
                                      border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
                                      background: hasSelection ? "#f6ffed" : "#fff1f0",
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 11,
                                        color: hasSelection ? "#389e0d" : "#cf1322",
                                        fontWeight: 600,
                                        display: "block",
                                        marginBottom: 6,
                                      }}
                                    >
                                      {hasSelection ? "✓ Attorney Name Selected" : "⚠ Select Attorney Name:"}
                                    </Text>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                      {opts.map((opt) => {
                                        const nameStr = typeof opt === "string" ? opt : opt.attorney_name;
                                        const isSelected = (selectedAttorneyNames[slot.value] || []).includes(nameStr);
                                        return (
                                          <div
                                            key={nameStr}
                                            onClick={() => onSelectAttorneyName && onSelectAttorneyName(slot.value, nameStr)}
                                            style={{
                                              cursor: "pointer",
                                              padding: "4px 10px",
                                              borderRadius: 6,
                                              fontSize: 12,
                                              fontWeight: isSelected ? 600 : 400,
                                              border: `1.5px solid ${isSelected ? "#52c41a" : "#d9d9d9"}`,
                                              background: isSelected ? "#f6ffed" : "#fafafa",
                                              color: isSelected ? "#389e0d" : "#595959",
                                              transition: "all 0.2s",
                                            }}
                                          >
                                            {nameStr}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </motion.div>
                                );
                              })()}
                            </Space>
                          </Col>

                          {/* TM Count */}
                          <Col xs={24} sm={24} md={8} lg={8}>
                            <Card
                              size="small"
                              className="text-center shadow-sm"
                              style={{
                                background: isValid
                                  ? "linear-gradient(135deg, #52c41a15 0%, #73d13d15 100%)"
                                  : "linear-gradient(135deg, #f0f0f0 0%, #fafafa 100%)",
                                borderColor: isValid ? "#52c41a" : "#d9d9d9",
                                borderWidth: 2,
                                borderRadius: 8,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                padding: "12px 8px",
                                minHeight: 90,
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  display: "block",
                                  marginBottom: 4,
                                }}
                              >
                                TM Count
                              </Text>
                              {isValid ? (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                  }}
                                >
                                  {/* Read-only — value comes from the status check */}
                                  <Text
                                    style={{
                                      fontSize: 28, fontWeight: 700, color: "#52c41a",
                                      textAlign: "center", display: "block", lineHeight: 1.1,
                                    }}
                                  >
                                    {Number(slot.tmCount || 0).toLocaleString("en-IN")}
                                  </Text>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 10, display: "block", marginTop: 2 }}
                                  >
                                    Trademarks
                                  </Text>
                                </motion.div>
                              ) : (
                                <div>
                                  <Text
                                    style={{
                                      fontSize: 28,
                                      color: "#bfbfbf",
                                      lineHeight: 1,
                                      display: "block",
                                      fontWeight: 600,
                                    }}
                                  >
                                    --
                                  </Text>
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 10,
                                      display: "block",
                                      marginTop: 2,
                                    }}
                                  >
                                    {isInvalid ? "Not Found" : "Pending"}
                                  </Text>
                                </div>
                              )}
                            </Card>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>

            {/* Check Status Button — validates existing + new empty slots */}
            {(emptySlots.length > 0 || existingAttorneys.length > 0) && (
              <div className="mt-4 pt-3 border-top" style={{ overflow: "hidden" }}>
                <Row gutter={16} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Space direction="vertical" size={2}>
                      <Text strong style={{ fontSize: 13 }}>
                        Ready to verify new attorney numbers?
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Click the button to validate the new attorney numbers
                        and fetch TM counts
                      </Text>
                    </Space>
                  </Col>
                  <Col xs={24} sm={8} md={6}>
                    <motion.div
                      whileHover={{
                        scale: isCheckingAttorney ? 1 : 1.03,
                      }}
                      whileTap={{
                        scale: isCheckingAttorney ? 1 : 0.97,
                      }}
                    >
                      <AntButton
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleCheckAttorneyStatus}
                        loading={isCheckingAttorney}
                        size="large"
                        block
                        style={{
                          background: isCheckingAttorney
                            ? "#bfbfbf"
                            : "linear-gradient(135deg, #722ed1 0%, #9254de 100%)",
                          borderColor: "transparent",
                          height: 48,
                          fontSize: 15,
                          fontWeight: 600,
                          boxShadow: "0 4px 12px rgba(114, 46, 209, 0.3)",
                          borderRadius: 8,
                        }}
                        disabled={
                          isCheckingAttorney ||
                          (existingAttorneys.length === 0 && emptySlots.every((s) => !s.value.trim()))
                        }
                      >
                        {isCheckingAttorney ? "Checking..." : "Check Status"}
                      </AntButton>
                    </motion.div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Stats bar */}
            {(existingAttorneys.length > 0 || validNewSlots.length > 0) && (
              <div
                className="mt-4 p-3 rounded"
                style={{
                  background:
                    "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)",
                  border: "1px solid #bae0ff",
                  borderRadius: 8,
                }}
              >
                <Row gutter={0} className="text-center">
                  <Col
                    span={8}
                    style={{ borderRight: "1px solid #bae0ff" }}
                  >
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#1677ff",
                      }}
                    >
                      {existingAttorneys.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      Active Attorneys
                    </div>
                  </Col>
                  <Col
                    span={8}
                    style={{ borderRight: "1px solid #bae0ff" }}
                  >
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#722ed1",
                      }}
                    >
                      {emptySlots.length}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      Empty Slots
                    </div>
                  </Col>
                  <Col span={8}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#52c41a",
                      }}
                    >
                      {(
                        existingAttorneys.reduce(
                          (s, a) => s + Number(a.tm_count || 0),
                          0,
                        ) +
                        emptySlots
                          .filter((s) => s.status === "valid")
                          .reduce((s, slot) => s + Number(slot.tmCount || 0), 0)
                      ).toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                      Total TM Count
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Card>
        </motion.div>
      </Col>
    );
  }

  // ── Create mode rendering (original UI) ─────────────────────────────────
  return (
    <Col xs={24}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          className="shadow-sm border"
          style={{
            background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
            borderRadius: 12,
            border: "2px solid #d3adf7",
          }}
        >
          {/* Header Section */}
          <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
            <div>
              <Title level={5} className="mb-1" style={{ color: "#531dab" }}>
                <InfoCircleOutlined
                  className="me-2"
                  style={{ color: "#722ed1" }}
                />
                {isCorporate
                  ? "Corporate TM Count"
                  : "Attorney Numbers Management"}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isCorporate
                  ? "Default TM count is 5 for Corporate plans"
                  : `Enter ${attorneyCount} attorney number(s) to check status and TM counts`}
              </Text>
            </div>
            <Tag
              color="purple"
              style={{
                fontSize: 14,
                padding: "6px 16px",
                fontWeight: 600,
                borderRadius: 20,
              }}
            >
              {isCorporate
                ? "5 TM"
                : `${attorneyCount} Attorney${attorneyCount > 1 ? "s" : ""}`}
            </Tag>
          </div>

          {/* Attorney Input Grid — Create Mode */}
          {!isCorporate && (
            <Row gutter={[16, 16]}>
              {attorneyNumbers?.map((value, index) => (
                <Col xs={24} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: index * 0.04,
                    }}
                  >
                    <Card
                      size="small"
                      className="shadow-sm"
                      style={{
                        backgroundColor:
                          attorneyValidation[value] === "valid"
                            ? "#f6ffed"
                            : attorneyValidation[value] === "invalid"
                              ? "#fff1f0"
                              : "#ffffff",
                        borderColor:
                          attorneyValidation[value] === "valid"
                            ? "#52c41a"
                            : attorneyValidation[value] === "invalid"
                              ? "#ff4d4f"
                              : "#d9d9d9",
                        borderWidth: 2,
                        borderRadius: 8,
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Row gutter={12} align="middle">
                        {/* Column 1: Attorney Number Input */}
                        <Col xs={24} sm={24} md={16} lg={16}>
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                            size={4}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <Text
                                strong
                                style={{
                                  fontSize: 13,
                                  color: "#595959",
                                }}
                              >
                                Attorney #{index + 1}
                              </Text>
                              {attorneyValidation[value] && (
                                <Tag
                                  color={
                                    attorneyValidation[value] === "valid"
                                      ? "success"
                                      : "error"
                                  }
                                  icon={
                                    attorneyValidation[value] === "valid" ? (
                                      <CheckCircleOutlined />
                                    ) : (
                                      <CloseCircleOutlined />
                                    )
                                  }
                                  style={{
                                    margin: 0,
                                    fontSize: 11,
                                  }}
                                >
                                  {attorneyValidation[value] === "valid"
                                    ? "Valid"
                                    : "Invalid"}
                                </Tag>
                              )}
                            </div>

                            <Input
                              size="large"
                              value={value}
                              onChange={(e) =>
                                handleAttorneyNumberChange(
                                  index,
                                  e.target.value,
                                )
                              }
                              maxLength={10}
                              placeholder={`Enter attorney number ${index + 1}`}
                              suffix={
                                isCheckingAttorney ? (
                                  <Spin size="small" />
                                ) : (
                                  <Tooltip
                                    title={
                                      attorneyValidationMessages[value] ||
                                      "Enter Attorney Number"
                                    }
                                  >
                                    {attorneyValidation[value] === "valid" ? (
                                      <CheckCircleOutlined
                                        style={{
                                          color: "#52c41a",
                                          fontSize: 20,
                                          animation:
                                            "pulse 1.5s ease-in-out infinite",
                                        }}
                                      />
                                    ) : attorneyValidation[value] ===
                                      "invalid" ? (
                                      <CloseCircleOutlined
                                        style={{
                                          color: "#ff4d4f",
                                          fontSize: 20,
                                        }}
                                      />
                                    ) : (
                                      <InfoCircleOutlined
                                        style={{
                                          color: "rgba(0,0,0,.45)",
                                          fontSize: 18,
                                        }}
                                      />
                                    )}
                                  </Tooltip>
                                )
                              }
                              status={
                                attorneyValidation[value] === "valid"
                                  ? ""
                                  : attorneyValidation[value] === "invalid"
                                    ? "error"
                                    : ""
                              }
                              style={{
                                borderWidth: 2,
                                borderRadius: 8,
                                fontSize: 15,
                                fontWeight: 500,
                              }}
                            />

                            {/* Validation Message */}
                            {attorneyValidation[value] && (
                              <motion.div
                                initial={{
                                  opacity: 0,
                                  height: 0,
                                }}
                                animate={{
                                  opacity: 1,
                                  height: "auto",
                                }}
                                transition={{
                                  duration: 0.2,
                                }}
                              >
                                <Text
                                  className="d-block"
                                  type={
                                    attorneyValidation[value] === "valid"
                                      ? "success"
                                      : "danger"
                                  }
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  <span
                                    style={{
                                      marginRight: 4,
                                    }}
                                  >
                                    {attorneyValidation[value] === "valid"
                                      ? "✓"
                                      : "✗"}
                                  </span>
                                  {attorneyValidationMessages[value]}
                                </Text>
                              </motion.div>
                            )}

                            {/* Attorney Name Selection */}
                            {attorneyValidation[value] === "valid" &&
                              attorneyNameOptions[value]?.length > 0 && (() => {
                                const hasSelection = (selectedAttorneyNames[value]?.length || 0) > 0;
                                return (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ duration: 0.2 }}
                                    style={{
                                      marginTop: 6,
                                      padding: "8px 10px",
                                      borderRadius: 6,
                                      border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
                                      background: hasSelection ? "#f6ffed" : "#fff1f0",
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 11,
                                        color: hasSelection ? "#389e0d" : "#cf1322",
                                        fontWeight: 600,
                                        display: "block",
                                        marginBottom: 6,
                                      }}
                                    >
                                      {hasSelection ? "✓ Attorney Name Selected" : "⚠ Select Attorney Name:"}
                                    </Text>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                      {attorneyNameOptions[value].map((opt) => {
                                        const isSelected =
                                          (selectedAttorneyNames[value] || []).includes(opt.attorney_name);
                                        return (
                                          <div
                                            key={opt.attorney_name}
                                            onClick={() =>
                                              onSelectAttorneyName &&
                                              onSelectAttorneyName(value, opt.attorney_name)
                                            }
                                            style={{
                                              cursor: "pointer",
                                              padding: "4px 10px",
                                              borderRadius: 6,
                                              fontSize: 12,
                                              fontWeight: isSelected ? 600 : 400,
                                              border: `1.5px solid ${isSelected ? "#52c41a" : "#d9d9d9"}`,
                                              background: isSelected ? "#f6ffed" : "#fafafa",
                                              color: isSelected ? "#389e0d" : "#595959",
                                              transition: "all 0.2s",
                                            }}
                                          >
                                            {opt.attorney_name}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </motion.div>
                                );
                              })()}
                          </Space>
                        </Col>

                        {/* Column 2: TM Count Display */}
                        <Col xs={24} sm={24} md={8} lg={8}>
                          <motion.div
                            initial={{
                              scale: 0.9,
                              opacity: 0,
                            }}
                            animate={{
                              scale: 1,
                              opacity: 1,
                            }}
                            transition={{
                              duration: 0.3,
                              delay: 0.1,
                            }}
                          >
                            <Card
                              size="small"
                              className="text-center shadow-sm"
                              style={{
                                background:
                                  attorneyValidation[value] === "valid"
                                    ? "linear-gradient(135deg, #52c41a15 0%, #73d13d15 100%)"
                                    : "linear-gradient(135deg, #f0f0f0 0%, #fafafa 100%)",
                                borderColor:
                                  attorneyValidation[value] === "valid"
                                    ? "#52c41a"
                                    : "#d9d9d9",
                                borderWidth: 2,
                                borderRadius: 8,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                padding: "12px 8px",
                                minHeight: 90,
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  display: "block",
                                  marginBottom: 4,
                                }}
                              >
                                TM Count
                              </Text>

                              {attorneyValidation[value] === "valid" ? (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: 0.2,
                                  }}
                                >
                                  {/* Read-only — value comes from the status check */}
                                  <Text
                                    style={{
                                      fontSize: 28, fontWeight: 700, color: "#52c41a",
                                      textAlign: "center", display: "block", lineHeight: 1.1,
                                    }}
                                  >
                                    {Number(attorneyTMCounts[value] || 0).toLocaleString("en-IN")}
                                  </Text>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 10, display: "block", marginTop: 2 }}
                                  >
                                    Trademarks
                                  </Text>
                                </motion.div>
                              ) : (
                                <div>
                                  <Text
                                    style={{
                                      fontSize: 28,
                                      color: "#bfbfbf",
                                      lineHeight: 1,
                                      display: "block",
                                      fontWeight: 600,
                                    }}
                                  >
                                    --
                                  </Text>
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 10,
                                      display: "block",
                                      marginTop: 2,
                                    }}
                                  >
                                    {attorneyValidation[value] === "invalid"
                                      ? "Not Found"
                                      : "Pending"}
                                  </Text>
                                </div>
                              )}
                            </Card>
                          </motion.div>
                        </Col>
                      </Row>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          )}

          {/* Corporate Info Message */}
          {isCorporate && (
            <Card
              size="small"
              className="text-center"
              style={{
                background: "linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)",
                borderColor: "#91d5ff",
                borderWidth: 2,
                borderRadius: 8,
                padding: "24px",
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <Text
                  strong
                  style={{
                    fontSize: 48,
                    color: "#1890ff",
                    display: "block",
                    lineHeight: 1,
                    fontWeight: 700,
                  }}
                >
                  5
                </Text>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 14,
                    display: "block",
                    marginTop: 8,
                  }}
                >
                  Default Trademark Count
                </Text>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    display: "block",
                    marginTop: 4,
                  }}
                >
                  Corporate plans include 5 TMs by default
                </Text>
              </motion.div>
            </Card>
          )}

          {/* Check Status Button — Create Mode */}
          {!isCorporate && (
            <div className="mt-4 pt-3 border-top" style={{ overflow: "hidden" }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={16} md={18}>
                  <Space direction="vertical" size={2}>
                    <Text strong style={{ fontSize: 13 }}>
                      Ready to verify attorney numbers?
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Click the button to validate all entered attorney numbers
                      and fetch TM counts
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <motion.div
                    whileHover={{
                      scale: isCheckingAttorney ? 1 : 1.03,
                    }}
                    whileTap={{
                      scale: isCheckingAttorney ? 1 : 0.97,
                    }}
                  >
                    <AntButton
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={handleCheckAttorneyStatus}
                      loading={isCheckingAttorney}
                      size="large"
                      block
                      style={{
                        background: isCheckingAttorney
                          ? "#bfbfbf"
                          : "linear-gradient(135deg, #722ed1 0%, #9254de 100%)",
                        borderColor: "transparent",
                        height: 48,
                        fontSize: 15,
                        fontWeight: 600,
                        boxShadow: "0 4px 12px rgba(114, 46, 209, 0.3)",
                        borderRadius: 8,
                      }}
                      disabled={
                        isCheckingAttorney ||
                        attorneyNumbers.every((n) => !n.trim())
                      }
                    >
                      {isCheckingAttorney ? "Checking..." : "Check Status"}
                    </AntButton>
                  </motion.div>
                </Col>
              </Row>
            </div>
          )}
        </Card>
      </motion.div>
    </Col>
  );
};

export default AttorneyNumbersSection;
